import { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Card, Badge, gbp, fmtDate, Loading } from "@/components/ui-bits";
import { Hammer, Check, X, PoundSterling, MessageSquare, Send, Paperclip, Loader2, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import ProjectThread from "@/components/ProjectThread";

const NEXT = { Offered: "Accepted", Accepted: "In Progress", "In Progress": "Completed" };

export default function ContractorPortal() {
  const [jobs, setJobs] = useState(null);
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [modal, setModal] = useState(null); // job for quote/invoice
  const [form, setForm] = useState({ quote_amount: "", invoice_amount: "", notes: "" });

  const load = () => api.get("/portal/contractor").then(({ data }) => { setJobs(data.jobs); setProjects(data.projects || []); setWorkOrders(data.work_orders || []); });
  useEffect(() => { load(); }, []);

  const patch = async (job, payload, msg) => {
    setJobs((prev) => prev.map((j) => j.job_id === job.job_id ? { ...j, ...payload } : j));
    await api.patch(`/portal/contractor/jobs/${job.job_id}`, payload);
    if (msg) toast.success(msg);
    load();
  };

  const openQuote = (job) => {
    setForm({ quote_amount: job.quote_amount || "", invoice_amount: job.invoice_amount || "", notes: job.notes || "" });
    setModal(job);
  };

  const saveQuote = async (e) => {
    e.preventDefault();
    await patch(modal, {
      quote_amount: parseFloat(form.quote_amount) || 0,
      invoice_amount: parseFloat(form.invoice_amount) || 0,
      notes: form.notes,
    }, "Job updated");
    setModal(null);
  };

  if (!jobs) return <PortalLayout title="My Jobs" icon={Hammer}><Loading /></PortalLayout>;

  const active = jobs.filter((j) => j.status !== "Completed");
  const done = jobs.filter((j) => j.status === "Completed");

  const JobCard = ({ j }) => (
    <Card className="p-5 fade-up" data-testid={`job-${j.job_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">{(j.property_address || j.property_ref) && <span className="text-xs text-blue-700">{j.property_address || j.property_ref}</span>}<Badge>{j.status}</Badge></div>
          <h3 className="font-semibold text-slate-900">{j.title}</h3>
        </div>
        <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0"><Hammer size={18} /></div>
      </div>
      <p className="text-sm text-slate-500">{j.description}</p>
      {(j.quote_amount > 0 || j.invoice_amount > 0) && (
        <div className="flex gap-4 mt-3 text-sm">
          {j.quote_amount > 0 && <span className="text-slate-600">Quote: <b className="tabular-nums">{gbp(j.quote_amount)}</b></span>}
          {j.invoice_amount > 0 && <span className="text-slate-600">Invoice: <b className="tabular-nums">{gbp(j.invoice_amount)}</b></span>}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2">Received {fmtDate(j.date)}</p>
      <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
        {NEXT[j.status] && (
          <button data-testid={`advance-job-${j.job_id}`} onClick={() => patch(j, { status: NEXT[j.status] }, `Marked ${NEXT[j.status]}`)}
            className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors">
            <Check size={15} /> {j.status === "Offered" ? "Accept Job" : `Mark ${NEXT[j.status]}`}
          </button>
        )}
        <button data-testid={`quote-job-${j.job_id}`} onClick={() => openQuote(j)} className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-1.5 transition-colors">
          <PoundSterling size={15} /> Quote / Invoice
        </button>
      </div>
      <JobComms jobId={j.job_id} />
    </Card>
  );

  return (
    <PortalLayout title="My Jobs" subtitle="Contractor" icon={Hammer}>
      {workOrders.length > 0 && (
        <>
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Job Requests</h3>
          <div className="grid lg:grid-cols-2 gap-5 mb-8">
            {workOrders.map((w) => <WorkOrderCard key={w.work_order_id} w={w} onChanged={load} />)}
          </div>
        </>
      )}
      <div className="grid lg:grid-cols-2 gap-5">
        {active.map((j) => <JobCard key={j.job_id} j={j} />)}
      </div>
      {active.length === 0 && workOrders.length === 0 && <p className="text-sm text-slate-400">No active jobs right now.</p>}

      {done.length > 0 && (
        <>
          <h3 className="font-display text-lg font-semibold text-slate-900 mt-8 mb-4">Completed</h3>
          <div className="grid lg:grid-cols-2 gap-5">{done.map((j) => <JobCard key={j.job_id} j={j} />)}</div>
        </>
      )}

      {projects.length > 0 && (
        <>
          <h3 className="font-display text-lg font-semibold text-slate-900 mt-8 mb-4">Project Discussions</h3>
          <div className="grid lg:grid-cols-2 gap-5">
            {projects.map((p) => (
              <Card key={p.project_id} className="p-5" data-testid={`con-project-${p.project_id}`}>
                <div className="flex items-center gap-2 mb-1">{(p.property_address || p.property_ref) && <span className="text-xs text-blue-700">{p.property_address || p.property_ref}</span>}<Badge>{p.type}</Badge></div>
                <h3 className="font-semibold text-slate-900">{p.name}</h3>
                <ConProjectQuote projectId={p.project_id} />
                <ProjectThread projectId={p.project_id} senderRole="contractor" />
              </Card>
            ))}
          </div>
        </>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5"><h3 className="font-display text-xl font-bold text-slate-900">Quote & Invoice</h3><button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button></div>
            <p className="text-sm text-slate-500 mb-4">{modal.title} · <span className="text-blue-700">{modal.property_address || modal.property_ref}</span></p>
            <form onSubmit={saveQuote} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Quote (£)</label><input data-testid="quote-amount-input" type="number" value={form.quote_amount} onChange={(e) => setForm({ ...form, quote_amount: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Invoice (£)</label><input data-testid="invoice-amount-input" type="number" value={form.invoice_amount} onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" /></div>
              <button data-testid="save-quote-button" type="submit" className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors">Save</button>
            </form>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}

function JobComms({ jobId, entityType = "job" }) {
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadComments = () => api.get(`/comments?entity_type=${entityType}&entity_id=${jobId}`).then(({ data }) => setComments(data)).catch(() => {});
  const loadAttachments = () => api.get(`/files?entity_type=${entityType}&entity_id=${jobId}&bucket=attachments`).then(({ data }) => setAttachments(data)).catch(() => {});

  useEffect(() => { loadComments(); loadAttachments(); /* eslint-disable-next-line */ }, [jobId]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/comments`, { entity_type: entityType, entity_id: jobId, body: text.trim() });
      setText("");
      await loadComments();
    } catch {
      toast.error("Could not send message");
    } finally {
      setSending(false);
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", entityType);
        fd.append("entity_id", jobId);
        fd.append("bucket", "attachments");
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded`);
      await loadAttachments();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const count = comments.length + attachments.length;

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`job-comms-${jobId}`}>
      <button data-testid={`toggle-comms-${jobId}`} onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-blue-700 transition-colors">
        <MessageSquare size={15} /> Communication
        {count > 0 && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{count}</span>}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {comments.length === 0 && attachments.length === 0 && (
              <p className="text-xs text-slate-400">No messages yet. Add a comment, ask a question, or upload photos/videos.</p>
            )}
            {comments.map((c) => (
              <div key={c.comment_id} data-testid={`comment-${c.comment_id}`}
                className={`rounded-lg px-3 py-2 text-sm ${c.author_role === "contractor" ? "bg-amber-50" : "bg-slate-50"}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-slate-800 text-xs">{c.author_name}</span>
                  <Badge>{c.author_role}</Badge>
                  <span className="text-[11px] text-slate-400 ml-auto">{fmtDate(c.date)}</span>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">{c.body}</p>
              </div>
            ))}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {attachments.map((f) => (
                  <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`job-att-${f.file_id}`}
                    className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center hover:ring-2 hover:ring-blue-500 transition-all">
                    {f.category === "photo" ? <img src={fileUrl(f.file_id)} alt="" className="w-full h-full object-cover" />
                      : f.category === "video" ? <video src={fileUrl(f.file_id)} className="w-full h-full object-cover" />
                      : <FileText size={18} className="text-slate-400" />}
                  </a>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={send} className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" data-testid={`comms-file-${jobId}`} onChange={onUpload} />
            <button type="button" data-testid={`comms-attach-${jobId}`} onClick={() => fileRef.current?.click()} disabled={uploading}
              className="w-9 h-9 shrink-0 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-60" title="Attach photos or videos">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />}
            </button>
            <input data-testid={`comms-input-${jobId}`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment or ask a question…"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="submit" data-testid={`comms-send-${jobId}`} disabled={sending || !text.trim()}
              className="w-9 h-9 shrink-0 rounded-lg bg-blue-700 text-white hover:bg-blue-800 flex items-center justify-center disabled:opacity-50">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}


const QB = { Submitted: "bg-blue-50 text-blue-700", Accepted: "bg-emerald-50 text-emerald-700", Declined: "bg-slate-100 text-slate-500 line-through" };
const WO_STAGES = ["Scheduled", "In Progress", "On Hold", "Completed"];
const STAGE_STYLE = { Scheduled: "bg-slate-100 text-slate-600", "In Progress": "bg-blue-100 text-blue-700", "On Hold": "bg-amber-100 text-amber-700", Completed: "bg-emerald-100 text-emerald-700" };

function WorkOrderCard({ w, onChanged }) {
  const [amount, setAmount] = useState(w.quote_amount || "");
  const [notes, setNotes] = useState(w.quote_notes || "");
  const [busy, setBusy] = useState(false);
  const [stageBusy, setStageBusy] = useState(false);
  const accepted = w.quote_status === "Accepted";
  const submit = async (e) => {
    e.preventDefault();
    if (!(parseFloat(amount) > 0)) { toast.error("Enter a quote amount"); return; }
    setBusy(true);
    try {
      await api.post(`/portal/contractor/work-orders/${w.work_order_id}/quote`, { quote_amount: parseFloat(amount), quote_notes: notes });
      toast.success("Quote submitted"); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit quote"); }
    finally { setBusy(false); }
  };
  const setStage = async (status) => {
    if (status === w.status) return;
    setStageBusy(true);
    try {
      await api.patch(`/portal/contractor/work-orders/${w.work_order_id}/status`, { status });
      toast.success(`Marked "${status}"`); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update progress"); }
    finally { setStageBusy(false); }
  };
  return (
    <Card className="p-5 fade-up" data-testid={`workorder-${w.work_order_id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-blue-700">{w.reference}</span>
            {(w.property_address || w.property_ref) && <Badge>{w.property_address || w.property_ref}</Badge>}
            {w.priority && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{w.priority}</span>}
            {accepted && <span data-testid={`wo-stage-badge-${w.work_order_id}`} className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${STAGE_STYLE[w.status] || "bg-slate-100 text-slate-600"}`}>{w.status}</span>}
          </div>
          <h3 className="font-semibold text-slate-900">{w.title}</h3>
        </div>
        <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center shrink-0"><Hammer size={18} /></div>
      </div>
      {w.description && <p className="text-sm text-slate-500">{w.description}</p>}

      {w.files?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`wo-files-${w.work_order_id}`}>
          {w.files.map((f) => (
            <a key={f.file_id} href={fileUrl(f.file_id)} target="_blank" rel="noreferrer"
              className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center hover:ring-2 hover:ring-blue-500 transition-all" title={f.original_filename}>
              {f.category === "photo" ? <img src={fileUrl(f.file_id)} alt="" className="w-full h-full object-cover" />
                : f.category === "video" ? <video src={fileUrl(f.file_id)} className="w-full h-full object-cover" />
                : <FileText size={18} className="text-slate-400" />}
            </a>
          ))}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><PoundSterling size={13} /> Your quote</p>
        {w.quote_status && (
          <div className="flex items-center gap-2 mb-2 text-sm">
            <span className="font-semibold tabular-nums text-slate-800">{gbp(w.quote_amount)}</span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${QB[w.quote_status] || "bg-slate-100 text-slate-600"}`}>{w.quote_status}</span>
            {w.quote_notes && <span className="text-xs text-slate-400 truncate">{w.quote_notes}</span>}
          </div>
        )}
        {accepted ? (
          <div data-testid={`wo-progress-${w.work_order_id}`}>
            <p className="text-sm text-emerald-700 font-medium flex items-center gap-1.5 mb-2"><Check size={15} /> Quote accepted — update your progress below.</p>
            <p className="text-[11px] font-medium text-slate-500 mb-1.5">Job progress</p>
            <div className="flex flex-wrap gap-1.5">
              {WO_STAGES.map((s) => (
                <button key={s} type="button" disabled={stageBusy} onClick={() => setStage(s)} data-testid={`wo-stage-${s.replace(/\s+/g, "-").toLowerCase()}-${w.work_order_id}`}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors disabled:opacity-60 ${w.status === s ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
            <input data-testid={`wo-quote-amount-${w.work_order_id}`} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Quote £" className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What's included" className="flex-1 min-w-[120px] rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <button type="submit" disabled={busy} data-testid={`wo-submit-quote-${w.work_order_id}`} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {w.quote_status === "Declined" ? "Re-quote" : w.quote_status === "Submitted" ? "Update quote" : "Submit quote"}</button>
          </form>
        )}
      </div>
      <JobComms jobId={w.work_order_id} entityType="work_order" />
    </Card>
  );
}

function ConProjectQuote({ projectId }) {
  const [quotes, setQuotes] = useState([]);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/projects/${projectId}/quotes`).then(({ data }) => setQuotes(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  const submit = async (e) => {
    e.preventDefault();
    if (!(parseFloat(amount) > 0)) { toast.error("Enter a quote amount"); return; }
    setBusy(true);
    try { await api.post(`/projects/${projectId}/quotes`, { amount: parseFloat(amount), notes }); toast.success("Quote submitted"); setAmount(""); setNotes(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`con-quote-${projectId}`}>
      <p className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><PoundSterling size={13} /> Your quotes</p>
      {quotes.map((q) => (
        <div key={q.quote_id} className="flex items-center gap-2 mb-1 text-sm">
          <span className="font-semibold tabular-nums text-slate-800">{gbp(q.amount)}</span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${QB[q.status]}`}>{q.status}</span>
          {q.notes && <span className="text-xs text-slate-400 truncate">{q.notes}</span>}
        </div>
      ))}
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 mt-2">
        <input data-testid={`con-quote-amount-${projectId}`} type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Quote £" className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Scope / breakdown" className="flex-1 min-w-[120px] rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        <button type="submit" disabled={busy} data-testid={`con-submit-quote-${projectId}`} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Submit quote</button>
      </form>
    </div>
  );
}
